// @ts-strict-ignore
"use client";
import type { CSSProperties } from "react";
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { Link as CoreLink } from "i18n/routing";
import storage from "config/storage";
import queryString from "query-string";

import { mixpanelClient } from "analytics/mixpanel";
import { isProductionEnv } from "config/utils";
import { CREDIT_CARD_BASE_URL, EXCHANGE_BASE_URL } from "config";
import { useSearchParams } from "next/navigation";
import { blockedUrls } from "utils/uk-fin-promo";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";

// upgrade guide https://nextjs.org/docs/upgrading#link-component
export interface NextLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children?: React.ReactNode;
  useReferrerPolicy?: boolean;
  id?: string;
  as?: string;
  title?: string;
  raw?: boolean;
  prefetch?: boolean;
  className?: string;
  newTab?: boolean;
  isAnchorLink?: boolean;
  i18n?: any;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const StyledA = styled.a`
  color: inherit;
  text-decoration: none;
  text-underline-offset: 4px;
  &:visited {
    color: inherit;
    text-decoration: inherit;
  }
`;

const StyledCore = styled.span`
  a {
    color: inherit;
    text-decoration: none;
    &:visited {
      color: inherit;
      text-decoration: inherit;
    }
  }
`;
const isAbsoluteUrl = (url: string) =>
  url.indexOf("://") > 0 ||
  url.indexOf("//") === 0 ||
  url.indexOf("mailto") === 0;

const Link = (props: NextLinkProps & { style?: CSSProperties }) => {
  const searchParams = useSearchParams();
  const clickId: string | undefined =
    storage.get("affiliateClickId", "sessionStorage") ||
    searchParams?.get("~click_id");

  const { country } = useCountryStateCookies();

  const [affilliateId, setAffilliateId] = React.useState(clickId);
  const {
    id,
    prefetch,
    raw,
    as,
    children,
    href,
    newTab,
    isAnchorLink,
    useReferrerPolicy = true,
    ...restProps
  } = props;

  useEffect(() => {
    if (affilliateId !== clickId) {
      setAffilliateId(clickId);
    }

    const isRegisterLink = href.includes("/register");

    if (!isProductionEnv && isRegisterLink && !id) {
      throw new Error(
        `You must add tracking for all registration links. This should take the form form of an id on the element that uses the name of the page and then a description of where that element is. For example: id="activetrader-hero-btn"`
      );
    }
    if (id) {
      mixpanelClient.trackLinkClick(id, isRegisterLink);
    }
  }, [affilliateId, clickId, href, id]);

  const hrefWithReferralCode = useMemo(() => {
    // Forward referralCode when navigating to either internal links or the exchange
    // Note: `referralCode` will only be present if a user is redirected to the homepage with a referralCode
    const isRegisterLink = href.includes("/register");
    const isCreditCardRegisterLink = href.includes("/credit-card/apply");

    const referralCode = searchParams?.get("referralCode");

    const hrefParts = href.split("?");

    const hrefParams = new URLSearchParams(hrefParts[1] || "");

    if (referralCode) hrefParams.set("referral", referralCode);
    if (affilliateId) hrefParams.set("~click_id", affilliateId);

    const queryParams =
      hrefParams.size > 0
        ? queryString.stringify({
            ...Object.fromEntries(hrefParams.entries())
          })
        : null;

    // fin promo requirement - blockedUrls var is specific urls
    if (country === "GB" && blockedUrls.includes(href)) {
      return `/uk${queryParams ? `?${queryParams}` : ""}`;
    }
    // need to redirect users from the country GB to the /staking-pro-uk page if they visit the /staking page
    if (country === "GB" && href.includes("/staking")) {
      return `/staking-pro-uk${queryParams ? `?${queryParams}` : ""}`;
    }
    if (isRegisterLink) {
      return `${EXCHANGE_BASE_URL}/register${queryParams ? `?${queryParams}` : ""}`;
    }
    if (isCreditCardRegisterLink) {
      return `${CREDIT_CARD_BASE_URL}/credit-card/apply${queryParams ? `?${queryParams}` : ""}`;
    }
    return href;
  }, [href, searchParams, affilliateId, country]);

  if (isAnchorLink) {
    return (
      <StyledA
        href={hrefWithReferralCode}
        id={id}
        {...restProps}
        target="_self"
      >
        {children}
      </StyledA>
    );
  }
  if (raw || href.includes("exchange.gemini.com")) {
    return (
      <StyledA
        href={hrefWithReferralCode}
        id={id}
        target={newTab ? "_blank" : "_self"}
        {...restProps}
      >
        {children}
      </StyledA>
    );
  }
  if (newTab && !(href && isAbsoluteUrl(href))) {
    return (
      <StyledCore>
        <CoreLink
          href={hrefWithReferralCode}
          id={id}
          as={as}
          prefetch={prefetch}
          passHref
          {...restProps}
          target="_blank"
        >
          {children}
        </CoreLink>
      </StyledCore>
    );
  }

  if ((href && isAbsoluteUrl(href)) || newTab) {
    const relValue = useReferrerPolicy ? "noopener noreferrer" : "noopener";
    return (
      <StyledA
        href={href}
        id={id}
        {...restProps}
        target="_blank"
        rel={relValue}
      >
        {children}
      </StyledA>
    );
  }

  return (
    <StyledCore>
      <CoreLink
        href={hrefWithReferralCode}
        id={id}
        as={as}
        prefetch={prefetch}
        passHref
        {...restProps}
      >
        {children}
      </CoreLink>
    </StyledCore>
  );
};

const LinkWrappedInSuspense = (
  props: NextLinkProps & { style?: CSSProperties } & {
    fallback?: React.ReactNode;
  }
) => {
  return (
    <React.Suspense fallback={props.fallback}>
      <Link {...props} />
    </React.Suspense>
  );
};

export default LinkWrappedInSuspense;
